import _ListItem from "@mui/material/ListItem";
import _ListItemText from "@mui/material/ListItemText";
import _Skeleton from "@mui/material/Skeleton";
import _ListItemAvatar from "@mui/material/ListItemAvatar";
import _Divider from "@mui/material/Divider";
import _TablePagination from "@mui/material/TablePagination";
import _List from "@mui/material/List";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
import _SentimentVeryDissatisfied from "@mui/icons-material/SentimentVeryDissatisfied";
import React from 'react';
import { useTranslation } from 'react-i18next';
import bronzeMedal from '../../../assets/images/medals/bronze.svg';
import goldMedal from '../../../assets/images/medals/gold.svg';
import silverMedal from '../../../assets/images/medals/silver.svg';
import { useChallenge } from '../../../hooks';
import { GeogroupAvatar } from '../../geogroup-avatar';
function LeaderboardList({
  event,
  highlightIndex,
  participantsCount,
  items,
  page,
  pageSize,
  setPage,
  setPageSize
}) {
  const {
    t
  } = useTranslation();
  if (items && items.length === 0) {
    return /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 3,
      paddingY: 10
    }, /*#__PURE__*/React.createElement(_SentimentVeryDissatisfied, {
      color: "action",
      sx: {
        height: 100,
        width: 100
      }
    }), /*#__PURE__*/React.createElement(_Typography, {
      align: "center",
      color: "textSecondary"
    }, t('commons.events.details.leaderboard.empty_state')));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_List, {
    disablePadding: true,
    id: "leaderboard",
    sx: {
      border: `1px solid #DDE0E7`,
      borderRadius: 2
    }
  }, items ? items.map((item, index) => /*#__PURE__*/React.createElement(LeaderboardListItem, {
    event: event,
    highlighted: index === highlightIndex,
    index: index,
    item: item,
    key: item.id
  })) : new Array(50).fill(null).map((_, index) => /*#__PURE__*/React.createElement(LeaderboardListItem, {
    event: event,
    index: index,
    key: index
  }))), /*#__PURE__*/React.createElement(_TablePagination, {
    component: _Box,
    count: participantsCount !== undefined ? participantsCount : -1,
    getItemAriaLabel: type => t(type === 'first' || type === 'previous' ? 'commons.table.prev_page' : 'commons.table.next_page'),
    labelDisplayedRows: ({
      from,
      to,
      count
    }) => t('commons.table.displayed_rows', {
      from,
      to,
      count
    }),
    labelRowsPerPage: t('commons.table.rows_per_page'),
    onPageChange: (_, newPage) => setPage(newPage + 1),
    onRowsPerPageChange: ({
      target: {
        value
      }
    }) => setPageSize(+value),
    page: page - 1,
    rowsPerPage: pageSize,
    rowsPerPageOptions: [50, 100]
  }));
}
function LeaderboardListItem({
  event,
  highlighted,
  index,
  item
}) {
  const {
    t
  } = useTranslation();
  const {
    getProgressValue,
    getProgressValueWithUnit
  } = useChallenge();
  const withoutPointsUnit = event?.challengeTemplate?.progressWeightingMode === 'EMPLOYEE_RATIO';
  return /*#__PURE__*/React.createElement(React.Fragment, null, index > 0 && /*#__PURE__*/React.createElement(_Divider, null), /*#__PURE__*/React.createElement(_ListItem, {
    id: `leaderboard-item-${index}`,
    sx: {
      flexWrap: 'wrap',
      rowGap: 0.5,
      ...(highlighted && {
        bgcolor: '#F3F6FF',
        transition: 'background-color 400ms ease'
      })
    }
  }, /*#__PURE__*/React.createElement(_ListItemAvatar, {
    sx: {
      alignItems: 'center',
      display: 'flex',
      gap: 2,
      minWidth: 104
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center",
    width: 24
  }, /*#__PURE__*/React.createElement(Rank, {
    index: index,
    item: item
  })), /*#__PURE__*/React.createElement(GeogroupAvatar, {
    geogroup: item?.parentTitle ? {
      title: item.parentTitle,
      photo: item.parentPhoto
    } : item,
    size: 48,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement(_ListItemText, {
    primary: item ? item.parentTitle ? `${item.parentTitle} - ${item.title}` : item.title : /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: 300
    }),
    secondary: item ? t('commons.communities.active_members', {
      count: item.activeParticipantsCount
    }) : /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: 200
    }),
    slotProps: {
      primary: {
        noWrap: true
      },
      secondary: {
        color: '#666A6F'
      }
    },
    sx: {
      minWidth: 140
    }
  }), (!event || event.challengeTemplate && event.challengeTemplate.type !== 'noTarget') && /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    marginLeft: {
      xs: 'auto',
      sm: 2
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "right",
    fontWeight: 700
  }, event?.challengeTemplate && item ? item.progressValue === 0 ? '-' : getProgressValueWithUnit(event.challengeTemplate.type, item.progressValue, {
    withoutPointsUnit
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 100
  })), !withoutPointsUnit && (!item || item.type !== 'city') && /*#__PURE__*/React.createElement(_Typography, {
    align: "right",
    color: "textSecondary",
    variant: "body2"
  }, event?.challengeTemplate && item ? t('commons.stats.on_average_active', {
    value: getProgressValue(event.challengeTemplate.type, item.activeParticipantsCount ? item.progressValue / item.activeParticipantsCount : 0, {
      withoutPointsUnit
    })
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 100
  })))));
}
function Rank({
  index,
  item
}) {
  if (!item) {
    if (index === 0 || index === 1 || index === 2) return /*#__PURE__*/React.createElement(_Skeleton, {
      height: 24,
      variant: "circular",
      width: 24
    });
    return /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: 24
    });
  }
  if (item.rank === 1) return /*#__PURE__*/React.createElement("img", {
    alt: "1",
    src: goldMedal,
    width: 24
  });
  if (item.rank === 2) return /*#__PURE__*/React.createElement("img", {
    alt: "2",
    src: silverMedal,
    width: 24
  });
  if (item.rank === 3) return /*#__PURE__*/React.createElement("img", {
    alt: "3",
    src: bronzeMedal,
    width: 24
  });
  return /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "body2"
  }, item.rank);
}
export default LeaderboardList;